import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useQuery } from "react-query";

import { CCard, CCardBody } from "@coreui/react";

import { dinhMucTonKhoApi } from "src/1/apis/dinh_muc_ton_kho.api";
import { nguyenVatLieuApi } from "src/1/apis/nguyen_vat_lieu.api";
import { CIconButton } from "src/1/common/components/controls";
import { CModal, CTable } from "src/1/common/components/others";
import { TrashIcon } from "src/common/assets/icons";
import { CButton, CNumberInput, CSelect } from "src/common/components/controls";
import { useGetMyStores } from "src/utils/hooks/options";

import "./styles.scss";

const defaultValues = {
  materials: [
    { nvl_id: "", from: 1, to: 4, normal: 40, holiday: 50, store_code: "" },
  ],
};

const WEEK_DAYS_OPTIONS = [
  { label: "Thứ 2", value: 1 },
  { label: "Thứ 3", value: 2 },
  { label: "Thứ 4", value: 3 },
  { label: "Thứ 5", value: 4 },
  { label: "Thứ 6", value: 5 },
  { label: "Thứ 7", value: 6 },
  { label: "Chủ nhật", value: 7 },
];

export const DinhMucTonKhoModal = forwardRef(({ refetch }, ref) => {
  //#region Data
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");

  const { data, isFetching } = useQuery({
    queryKey: ["chi-tiet-dinh-muc-ton-kho", id, open],
    queryFn: () => dinhMucTonKhoApi.getById(id),
    enabled: !!id && open,
    select: (response) => response?.data?.data,
  });

  const { data: materials = [] } = useQuery({
    queryKey: ["danh-sach-toan-bo-nvl"],
    queryFn: () => nguyenVatLieuApi.getAll(),
    select: (response) => response?.data?.data,
  });

  const { stores } = useGetMyStores();

  const {
    control,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "materials",
    keyName: "__id",
  });

  useEffect(() => {
    if (data) {
      replace([{ ...data }]);
    }
  }, [data]);
  //#endregion

  //#region Event
  const onClose = () => {
    setOpen(false);
    reset(defaultValues);
    setId("");
  };

  const onAddRow = () => {
    append({
      nvl_id: "",
      from: 1,
      to: 4,
      normal: 40,
      holiday: 50,
      store_code: "",
    });
  };

  //#endregion

  useImperativeHandle(ref, () => ({
    open: (_id) => {
      if (_id) {
        setId(_id);
      }
      setOpen(true);
    },
  }));

  //#region Render
  const headers = [
    {
      key: "nvl_id",
      label: "ID NVL",
      width: 220,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.nvl_id`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="code"
              select="code"
              options={materials}
            />
          )}
        />
      ),
    },
    {
      key: "nvl_id",
      label: "Tên NVL",
      width: 220,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.nvl_id`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="name"
              select="code"
              options={materials}
            />
          )}
        />
      ),
    },
    {
      key: "nvl_id",
      label: "ĐVT",
      width: 140,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.nvl_id`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="unit"
              select="code"
              readOnly
              options={materials}
            />
          )}
        />
      ),
    },
    {
      key: "from",
      label: "Từ Thứ",
      width: 160,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.from`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="label"
              select="value"
              options={WEEK_DAYS_OPTIONS}
            />
          )}
        />
      ),
    },
    {
      key: "to",
      label: "Đến Thứ",
      width: 160,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.to`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="label"
              select="value"
              options={WEEK_DAYS_OPTIONS}
            />
          )}
        />
      ),
    },
    {
      key: "normal",
      label: "Ngày Thường",
      width: 110,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.normal`}
          render={({ field }) => <CNumberInput {...field} />}
        />
      ),
    },
    {
      key: "holiday",
      label: "Ngày Lễ",
      width: 110,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.holiday`}
          render={({ field }) => <CNumberInput {...field} />}
        />
      ),
    },
    {
      key: "store_code",
      label: "Cửa Hàng",
      width: 220,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`materials.${index}.store_code`}
          render={({ field }) => (
            <CSelect
              {...field}
              display="label"
              select="value"
              options={stores}
            />
          )}
        />
      ),
    },
    {
      key: "action",
      label: "Thao Tác",
      width: 85,
      cellRender: (value, record, index) => (
        <div className="flex items-center justify-center">
          <CIconButton
            color="error"
            onClick={() => remove(index)}
            icon={<TrashIcon className="!size-5" />}
          />
        </div>
      ),
    },
  ];
  return (
    <CModal
      open={open}
      onClose={onClose}
      title={`${id ? "Sửa" : "Thêm"} Định Mức Tồn Kho`}
      loading={isSubmitting}
      fetching={isFetching}
      size="2xl"
    >
      <CCard>
        <CCardBody className="p-0">
          <CTable headers={headers} data={fields} height={400} rowKey="__id" />
        </CCardBody>

        <CButton onClick={onAddRow}>+ Thêm dòng</CButton>
      </CCard>
    </CModal>
  );
  //#endregion
});
